import React from 'react';
import config from '../common/config';
import {
	Avatar,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	ListItemIcon,
	Menu,
	MenuItem,
	TextField,
	Tooltip,
	Typography,
	useMediaQuery
} from '@mui/material';
import {Theme} from '../types';
import {css} from '@emotion/css';
import {useGlassBarClass} from '../hooks/useGlassBarClass';
import {Logout} from '@mui/icons-material';
import {useThemeContext} from '../ThemeProvider';
import useAuth from './Auth';
import KeyIcon from '@mui/icons-material/Key';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useAppDispatch, useAppSelector } from '../hooks';
import { isAccountDialogOpenSetState, isIntroDialogOpenSetState, selectIsAccountDialogOpen } from '../containers/authSlice';
import { selectAllEntries } from '../containers/contentSlice';
import { useUpdateCurrentAccountMutation } from '../services/accounts';
import SettingsIcon from '@mui/icons-material/Settings';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import ReactGA from 'react-ga4';
import { SHORT_SCREEN_MEDIA_QUERY } from '../constants';
import {NEXT_THEME_PREFERENCE, THEME_PREFERENCE_DISPLAY} from '../common/themePreference';

const classes = {
	root: css`
		font-size: 18px;
		letter-spacing: 8px;
		white-space: nowrap;
	`,
};

const LegalLink = styled.a`
  color: ${({ theme }) => (theme as Theme).palette.text.primary};
  text-decoration: none;
`;

const AccountMenu = ({areToolbarPointerEventsDisabled}: {areToolbarPointerEventsDisabled: boolean}) => {
	const isMdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
	const isShortScreen = useMediaQuery(SHORT_SCREEN_MEDIA_QUERY);
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const {themePreference, setThemePreference, isDarkModeOn} = useThemeContext();
	const {userProfile, logout, isAuthenticated, loginWithRedirect} = useAuth();
	const dispatch = useAppDispatch();
	const allEntries = useAppSelector(selectAllEntries);
	const isAccountDialogOpen = useAppSelector(selectIsAccountDialogOpen);
	const showDataPresets = !isAuthenticated || allEntries.length === 0;
	const [accountName, setAccountName] = React.useState('');
	const [accountError, setAccountError] = React.useState<string | null>(null);
	const [updateCurrentAccount, {isLoading: isUpdatingAccount}] = useUpdateCurrentAccountMutation();
	const glassBarClass = useGlassBarClass();

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => {
		setAnchorEl(null);
	};
	const handleLogoutClick = () => {
		logout();
	};
	const onDataPresetsClick = () => {
		setAnchorEl(null);
		dispatch(isIntroDialogOpenSetState(true));
	};
	const onAccountNameClick = () => {
		setAnchorEl(null);
		setAccountError(null);
		setAccountName(userProfile.name ?? '');
		dispatch(isAccountDialogOpenSetState(true));
	};
	const onAccountDialogClose = () => {
		if (!isUpdatingAccount) {
			dispatch(isAccountDialogOpenSetState(false));
		}
	};

	const onAccountSave = async () => {
		const trimmedName = accountName.trim();
		if (!trimmedName) {
			setAccountError('Name cannot be empty.');
			return;
		}
		try {
			if (trimmedName !== userProfile.name) {
				await updateCurrentAccount({name: trimmedName}).unwrap();
			}
		} catch {
			setAccountError('Could not update name. Please try again.');
			return;
		}
		dispatch(isAccountDialogOpenSetState(false));
	};

	const handleThemePreferenceClick = () => {
		const nextPreference = NEXT_THEME_PREFERENCE[themePreference];
		setThemePreference(nextPreference);
		ReactGA.event({
			category: 'UI',
			action: `ThemePreferenceChanged: ${nextPreference}`,
		});
	};

	const handleLoginClick = () => {
		loginWithRedirect();
		ReactGA.event({
			category: 'Authentication',
			action: 'Login',
		});
	};

	let initials = '';
	if (isAuthenticated) {
		initials = `${userProfile.name[0]}${userProfile.name[1]}`.toUpperCase();
	}

	return <>
		{/* On short screens the header's own padding alone sets the edge distance, matching the bottom-right action buttons. */}
		<Box display='flex' justifyContent='right' mr={isShortScreen ? 0 : {
			xs: -1,
			sm: 1.15,
			userSelect: 'none',
		}}>
			<Box display='flex' alignItems='center' className={glassBarClass} sx={{pl: isMdUp ? 2 : 0}}>
				{isMdUp && <Typography variant='body2' className={classes.root} sx={{letterSpacing: 6}}>{isAuthenticated ? userProfile.companyName : config.appName}</Typography>}
				<Tooltip title="Account settings" disableInteractive>
					<IconButton
						onClick={handleClick}
						aria-controls={open ? 'account-menu' : undefined}
						aria-haspopup="true"
						aria-expanded={open ? 'true' : undefined}
						tabIndex={-1}
						sx={{...(areToolbarPointerEventsDisabled ? {pointerEvents: 'all'} : {})}}
					>
						{isDarkModeOn ? <img src='/logo-feather-dark.svg' width='32' /> : <img src='/logo-feather.svg' width='32' />}
					</IconButton>
				</Tooltip>
			</Box>
		</Box>
		<Menu
			anchorEl={anchorEl}
			id="account-menu"
			open={open}
			onClose={handleClose}
		>
			{isAuthenticated && <MenuItem onClick={() => {
				onAccountNameClick();
			}}>
				<Avatar sx={{mr: 1}}>{initials}</Avatar>
				<Typography
					variant='body2'
				>
					{userProfile.name}
				</Typography>
			</MenuItem>}
			{!isAuthenticated && <MenuItem onClick={handleLoginClick}>
				<ListItemIcon>
					<KeyIcon fontSize="small"/>
				</ListItemIcon>
				Sign in
			</MenuItem>}
			<Divider />
			<MenuItem component={Link} to='/settings' onClick={handleClose}>
				<ListItemIcon>
					<SettingsIcon fontSize="small"/>
				</ListItemIcon>
				Settings
			</MenuItem>
			{isAuthenticated && <Divider />}
			{isAuthenticated && <MenuItem onClick={handleLogoutClick}>
				<ListItemIcon>
					<Logout fontSize="small" />
				</ListItemIcon>
				Sign out
			</MenuItem>}
			<Divider />
			{showDataPresets && <MenuItem component={Link} to='/' onClick={onDataPresetsClick}>
				<ListItemIcon>
					<HelpOutlineIcon fontSize="small"/>
				</ListItemIcon>
				Get started
			</MenuItem>}
			<MenuItem onClick={handleThemePreferenceClick}>
				<ListItemIcon>
					{THEME_PREFERENCE_DISPLAY[themePreference].icon}
				</ListItemIcon>
				{THEME_PREFERENCE_DISPLAY[themePreference].label}
			</MenuItem>
			<Divider />
			{!isAuthenticated && <MenuItem>
				<LegalLink href='https://www.untwyn.com' target='_blank' rel='noopener noreferrer'>
				What is Untwyn?
				</LegalLink>
			</MenuItem>}
			<MenuItem>
				<LegalLink href='https://www.untwyn.com/terms-and-conditions' target='_blank' rel='noopener noreferrer'>
				Terms &amp; Conditions
				</LegalLink>
			</MenuItem>
			<MenuItem>
				<LegalLink href='https://www.untwyn.com/privacy-policy' target='_blank' rel='noopener noreferrer'>
				Privacy Policy
				</LegalLink>
			</MenuItem>
			<MenuItem>
				<LegalLink href='https://www.untwyn.com/docs' target='_blank' rel='noopener noreferrer'>
				Documentation
				</LegalLink>
			</MenuItem>
			<Box display='flex' justifyContent='center'>
				<Typography variant='caption' color='text.secondary' align='center'>v{config.appVersion}</Typography>
			</Box>
		</Menu>
		<Dialog
			open={isAccountDialogOpen}
			onClose={onAccountDialogClose}
			maxWidth='xs'
			fullWidth
		>
			<form onSubmit={(event) => {
				event.preventDefault();
				onAccountSave();
			}}>
				<DialogTitle>My account</DialogTitle>
				<DialogContent>
					<TextField
						autoFocus
						margin='dense'
						label='Name'
						fullWidth
						value={accountName}
						onChange={(event) => {
							setAccountError(null);
							setAccountName(event.target.value);
						}}
						error={Boolean(accountError)}
						helperText={accountError ?? ' '}
						disabled={isUpdatingAccount}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={onAccountDialogClose} disabled={isUpdatingAccount}>Cancel</Button>
					<Button type='submit' variant='contained' disabled={isUpdatingAccount}>Save</Button>
				</DialogActions>
			</form>
		</Dialog>
	</>;
};

export default AccountMenu;
